// @ts-strict-ignore
import React, { useCallback, useMemo, useRef, useState } from 'react'

import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'

import { formatEventDate } from '../../utils/calendarUtils'

import CalendarEventPopup from './CalendarEventPopup'
import getCalendarData, { CalData } from './getCalendarData'

import './CalendarStyles.css'

type DataRow = {
    original: any
    [x: string]: any
}

type Data = {
    row: DataRow
    [x: string]: any
}

type FilterObject = {
    startDate: string
    endDate: string
    selectedDate: string
    currentView: string
}

type CalendarProps = {
    object: any
    data: any
    startDate: string
    endDate: string
    setCalendarFilter: (data: FilterObject) => void
    calendarFilter: FilterObject
    rowLink: (row: any) => void
    weekStartDay?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined
}

const CALENDAR_VIEWS = {
    Day: { type: 'timeGridDay', buttonText: 'Day' },
    Week: { type: 'timeGridWeek', buttonText: 'Week' },
    WorkWeek: { type: 'timeGridWeek', weekends: false, buttonText: 'Work Week' },
    Month: { type: 'dayGridMonth', buttonText: 'Month' },
    Agenda: { type: 'listWeek', buttonText: 'Agenda' },
} as const

type CalendarViewType = keyof typeof CALENDAR_VIEWS

const mapViewType = (viewType: string): CalendarViewType => {
    const viewMap: Record<string, CalendarViewType> = {
        agenda: 'Agenda',
        day: 'Day',
        week: 'Week',
        workweek: 'WorkWeek',
        month: 'Month',
    }
    return viewMap[viewType.toLowerCase()] || 'Month'
}

// Responsive hook to detect mobile or large screens
function useIsBelowWidth(breakpoint = 1024) {
    const [isBelow, setIsBelow] = React.useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    )
    React.useEffect(() => {
        const handler = () => setIsBelow(window.innerWidth < breakpoint)
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [breakpoint])
    return isBelow
}

const CalendarView = ({
    object,
    data,
    startDate,
    endDate,
    setCalendarFilter,
    calendarFilter,
    rowLink,
    weekStartDay = 0,
}: CalendarProps) => {
    const [popupEvent, setPopupEvent] = useState<any>(null)
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
    const calendarRef = useRef<any>(null)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const startDateField = object.fields.find((x) => x._sid === startDate)
    const endDateField = object.fields.find((x) => x._sid === endDate)

    const startDateApiName = startDateField?.api_name

    const startDateTimezone = startDateField?.options?.timezone === 'UTC' ? 'UTC' : 'local'
    const endDateApiName = endDateField?.api_name

    const calendarData = useMemo((): CalData[] => {
        return data.map((d: Data) => {
            const record = d?.row?.original || {}
            return {
                ...record,
                link: rowLink(d.row),
            }
        })
    }, [data, rowLink])

    const calendarDataSource = useMemo(
        () => getCalendarData(calendarData, startDateApiName, endDateApiName),
        [calendarData, endDateApiName, startDateApiName]
    )

    const handleDatesSet = useCallback(
        (arg) => {
            const startDate = arg.start
            const endDate = arg.end
            const currentView = arg.view.type
            const selectedDate = arg.view.currentStart

            setCalendarFilter({
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                selectedDate: selectedDate.toISOString(),
                currentView,
            })
        },
        [setCalendarFilter]
    )

    const handleEventClick = useCallback(
        (arg) => {
            const event = arg.event
            const eventLink = event.extendedProps.link

            // Get the click position for the popup
            const rect = arg.el.getBoundingClientRect()
            setPopupPosition({
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            })

            const dateDisplay = formatEventDate(event, startDateTimezone)

            setPopupEvent({
                title: event.title || '(No Title)',
                date: dateDisplay,
                link: eventLink,
            })
        },
        [startDateTimezone]
    )

    const handlePopupClose = useCallback(() => setPopupEvent(null), [])

    const isBelowLarge = useIsBelowWidth(1024)

    const handleViewChange = useCallback((viewType: string) => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi()
            calendarApi.changeView(viewType)
            setIsDropdownOpen(false)
        }
    }, [])

    const headerToolbar = useMemo(() => {
        return isBelowLarge
            ? {
                  left: 'prevYear,prev,next,nextYear',
                  center: 'title',
                  right: 'today viewMenu',
              }
            : {
                  left: 'prevYear,prev,next,nextYear',
                  center: 'title',
                  right: 'today Day,WorkWeek,Week,Month,Agenda',
              }
    }, [isBelowLarge])

    const dayHeaderFormat: { weekday: 'short' | 'long' } = useMemo(
        () => (isBelowLarge ? { weekday: 'short' } : { weekday: 'long' }),
        [isBelowLarge]
    )

    return (
        <div className="calendar-container">
            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView={mapViewType(calendarFilter.currentView)}
                initialDate={calendarFilter.selectedDate}
                firstDay={weekStartDay}
                height="100%"
                events={calendarDataSource}
                datesSet={handleDatesSet}
                eventClick={handleEventClick}
                headerToolbar={headerToolbar}
                views={CALENDAR_VIEWS}
                buttonText={{ today: 'Today' }}
                dayHeaderFormat={dayHeaderFormat}
                eventTimeFormat={{
                    hour: 'numeric',
                    minute: '2-digit',
                    meridiem: 'short',
                }}
                timeZone={startDateTimezone}
                editable={false}
                selectable={false}
                selectMirror={true}
                dayMaxEvents={true}
                dayMaxEventRows={true}
                fixedWeekCount={false}
                showNonCurrentDates={true}
                dayCellClassNames="calendar-day-cell"
                slotLabelClassNames="calendar-slot-label"
                dayHeaderClassNames="calendar-day-header"
                viewClassNames="calendar-view"
                eventClassNames="calendar-event"
                allDayClassNames="calendar-all-day"
                moreLinkClassNames="calendar-more-link"
                nowIndicatorClassNames="calendar-now-indicator"
                slotEventOverlap={false}
                customButtons={{
                    viewMenu: {
                        text: '⋮',
                        click: () => setIsDropdownOpen(!isDropdownOpen),
                    },
                }}
            />
            {isBelowLarge && isDropdownOpen && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: '40px',
                        right: '8px',
                        zIndex: 9999,
                        backgroundColor: 'white',
                        minWidth: '13.75rem',
                        padding: '4px 0',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        boxShadow: '0px 1px 12px 0px rgba(0, 0, 0, 0.14)',
                    }}
                >
                    {Object.entries(CALENDAR_VIEWS).map(([_, value]) => (
                        <div
                            key={value.type}
                            onClick={() => handleViewChange(value.type)}
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                                color: '#374151',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F3F4F6'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white'
                            }}
                        >
                            {value.buttonText}
                        </div>
                    ))}
                </div>
            )}
            {popupEvent && (
                <CalendarEventPopup
                    title={popupEvent.title}
                    date={popupEvent.date}
                    link={popupEvent.link}
                    position={popupPosition}
                    onClose={handlePopupClose}
                />
            )}
        </div>
    )
}

export default React.memo(CalendarView)
